// Banana Peel (§3.3). PEEL entities sit on the ground for `lifeTicks` (age = `t`, `data` = placed
// tick low bits); at most `maxOnField` exist (placing one more removes the oldest). A grounded body
// whose centre comes within `triggerRadius` slips: SLIP for 60 ticks at max(|v|, 5) along its
// velocity (facing if still), +10 Dizzy, credited to the peel's owner (cause PEEL, aux = the peel's
// age), and the peel is consumed. The owner is immune to its own peel for its first 60 ticks;
// airborne bodies pass over.
//
// Predict mode (§18.2): no peel is placed, peels never expire, but the local bean's own slip is
// predicted and consumes the peel in the predicted world.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK } from '../ids';
import { MAPS } from '../maps/index';
import { MODES } from '../modes/index';
import type { Bean, Entity, World } from '../types';
import { releaseCarry } from './carry';
import { bodyCollides } from './collision';
import { addDizzy } from './combat';
import { dcos, dsin } from './dmath';
import { isGrounded } from './edges';
import { removeEntity, removeEntityAt, spawnEntity } from './entities';
import { STATE_TABLE } from './stateTable';
import { isPredicting, isPuppet, isTeammate, refreshTouch, setTouch } from './world';

/** Item use: drops a peel 0.7 m behind the bean (lost over the void), retiring the oldest beyond the field cap. */
export function placePeel(w: World, b: Bean): void {
  if (isPredicting(w)) return;
  const k = TUNE.banana;
  const x = b.pos.x - dcos(b.facing) * k.placeBehind;
  const y = b.pos.y - dsin(b.facing) * k.placeBehind;
  const g = MAPS[w.cfg.mapId].groundAt(w, x, y);
  if (g === VOID) return;
  for (;;) {
    let count = 0;
    let oldest = -1;
    for (let i = 0; i < w.ents.length; i++) {
      const e = w.ents[i];
      if (e.kind !== EK.PEEL) continue;
      count++;
      if (oldest < 0 || e.t > w.ents[oldest].t) oldest = i;
    }
    if (count < k.maxOnField) break;
    removeEntityAt(w, oldest);
  }
  const e = spawnEntity(w, EK.PEEL, b.slot, x, y, g);
  e.data = w.tick & 0xff;
  e.timer = k.lifeTicks;
}

/** Bodies that can slip: grounded, free bodies in states with an edge rule, not invulnerable. */
function canSlip(b: Bean): boolean {
  return bodyCollides(b) && STATE_TABLE[b.state].edge !== 'none' && isGrounded(b) && b.vz <= 0 && b.invulnTicks === 0;
}

function slip(w: World, b: Bean, e: Entity): void {
  const k = TUNE.banana;
  if (b.state === BS.CARRYING) releaseCarry(w, b, 'hit');
  let speed = Math.hypot(b.vel.x, b.vel.y);
  if (b.state === BS.LUNGE) speed = Math.min(speed, TUNE.bean.runSpeed);
  const ux = speed > 1e-6 ? b.vel.x / Math.hypot(b.vel.x, b.vel.y) : dcos(b.facing);
  const uy = speed > 1e-6 ? b.vel.y / Math.hypot(b.vel.x, b.vel.y) : dsin(b.facing);
  speed = Math.max(speed, k.slipMinSpeed);
  b.vel.x = ux * speed;
  b.vel.y = uy * speed;
  b.state = BS.SLIP;
  b.st = 0;
  b.partner = NO_SLOT;
  b.lastHitTick = w.tick;
  const owner = e.owner;
  if (!isTeammate(w, owner, b.slot)) {
    addDizzy(w, b, k.dizzy);
    // Touch credit is server state (§20.4); a self-slip claims none.
    if (!isPredicting(w)) {
      if (owner !== b.slot && owner !== NO_SLOT) setTouch(w, b.slot, owner, Cause.PEEL, e.t);
      else refreshTouch(w, b, Cause.PEEL);
    }
  }
  emit(w, EV.PEEL_SLIP, b.slot, owner, e.t, 0, e.pos.x, e.pos.y);
  removeEntity(w, e.id);
  if (!isPredicting(w)) MODES[w.cfg.modeId].onHit(w, owner, b.slot, Cause.PEEL);
}

/** Entity phase for a PEEL: expiry (server), then the first body to step on it slips. */
export function stepPeel(w: World, e: Entity): void {
  const predict = isPredicting(w);
  if (!predict && e.t > TUNE.banana.lifeTicks) {
    removeEntity(w, e.id);
    return;
  }
  const k = TUNE.banana;
  for (const b of w.beans) {
    if (!b.active || isPuppet(w, b.slot) || !canSlip(b)) continue;
    if (b.slot === e.owner && e.t <= k.ownerImmuneTicks) continue;
    if (Math.hypot(b.pos.x - e.pos.x, b.pos.y - e.pos.y) > k.triggerRadius) continue;
    slip(w, b, e);
    return;
  }
}
